/**
 * FieldBlock — klocek pola w canvasie kreatora modułu.
 *
 * F12.N pakiet B Sub-tura 1: wynosi inline JSX z `SystemModuleBuilderPage`
 * (linia ~2939) do osobnego komponentu, dzięki czemu klocek pola wygląda
 * identycznie czy jest renderowany w sekcji czy jako dziecko panelu akordeonu
 * (przez `renderChild` w `AccordionInCanvas`).
 *
 * Klocek zawiera (od lewej):
 *  - DragHandle (6 kropek) — Sub-tura 2 dopiero podepnie gest dnd-kit
 *  - nazwa pola + slug + fieldType
 *  - delete button (po prawej, hover-only przez group-hover w parent)
 */

import { type CSSProperties, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Eye, EyeOff, X } from 'lucide-react';

import { IconButton } from '@/components/IconButton';

import { DragHandle } from './DragHandle';

export interface FieldBlockField {
  id: string;
  name: string;
  slug: string;
  fieldType: string;
}

export interface FieldBlockProps {
  /** ID elementu w drzewie layoutu (NIE field.id). Używane przy delete + sortable. */
  elementId: string;
  field: FieldBlockField;
  onRemove: (elementId: string) => void;
  /**
   * Spread z `useSortable.attributes` + `useSortable.listeners`. Używany w testach
   * legacy które renderują FieldBlock bez SortableContext rodzica. Gdy `sortableContainerId`
   * jest ustawione — `dragHandleProps` jest ignorowane i useSortable woła się wewnątrz.
   */
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
  /**
   * ID rodzica (sekcja lub panel akordeonu) — gdy ustawione, FieldBlock wpina `useSortable`
   * z `data: { source: 'element', target: 'section', sectionId: <containerId> }`. Pakiet B Sub-2.
   * Bez tego FieldBlock działa jak statyczny klocek (parytet z Sub-1 — wizualnie + struct).
   */
  sortableContainerId?: string;
  /** Zaznaczony w kreatorze — pokazuje ring i otwiera panel właściwości pola po prawej. */
  selected?: boolean;
  /** Klik w klocek (poza uchwytem/usuwaniem) zaznacza pole. */
  onSelect?: () => void;
  /**
   * Layout SYSTEMOWY: zamiast uchwytu + usuwania klocek pokazuje TYLKO ikonkę oka
   * (ukryj/pokaż). Canvas jest wtedy inertny (pointerEvents:none), więc oko dostaje
   * `pointerEvents:auto` (jak pasek zakładek). `false`/brak = własny layout (drag + delete).
   */
  systemHideOnly?: boolean;
  /** Czy pole jest ukryte nakładką per-tenant (systemowy layout) — wyszarza klocek. */
  hidden?: boolean;
  /** Ukryj/pokaż pole (ikonka oka) — wymagane gdy `systemHideOnly`. */
  onToggleHidden?: (elementId: string) => void;
}

export function FieldBlock({
  elementId,
  field,
  onRemove,
  dragHandleProps,
  sortableContainerId,
  selected,
  onSelect,
  systemHideOnly = false,
  hidden = false,
  onToggleHidden,
}: FieldBlockProps) {
  const { t } = useTranslation();
  const sortable = useSortable({
    id: elementId,
    data: { source: 'element', target: 'section', sectionId: sortableContainerId },
    // Disabled gdy parent nie chce sortable (legacy fixturki testów).
    disabled: !sortableContainerId,
  });
  const style: CSSProperties = sortableContainerId
    ? {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.5 : undefined,
      }
    : {};
  const effectiveHandleProps = sortableContainerId
    ? { ...sortable.attributes, ...sortable.listeners }
    : dragHandleProps;
  return (
    <div
      ref={sortableContainerId ? sortable.setNodeRef : undefined}
      style={style}
      onClick={
        onSelect
          ? (e) => {
              e.stopPropagation();
              onSelect();
            }
          : undefined
      }
      className={
        'flex items-center justify-between gap-2 rounded border bg-bg-secondary px-3 py-2 ' +
        (onSelect ? 'cursor-pointer ' : '') +
        (systemHideOnly && hidden ? 'opacity-60 ' : '') +
        (selected ? 'border-accent-500 ring-2 ring-accent-500' : 'border-border-primary')
      }
      data-testid={`system-module-builder-element-${field.slug}`}
      data-hidden={systemHideOnly && hidden ? 'true' : 'false'}
    >
      {/* Uchwyt przeciągania — tylko w trybie pełnej edycji (własny layout). */}
      {!systemHideOnly && (
        <DragHandle
          ariaLabel={t('tenant.systemModuleBuilder.dragField', {
            defaultValue: 'Przeciągnij pole',
          })}
          dragHandleProps={effectiveHandleProps as HTMLAttributes<HTMLDivElement>}
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-text-primary">{field.name}</p>
        <p className="truncate text-[10px] text-text-tertiary">
          {field.slug} · {field.fieldType}
        </p>
      </div>
      {systemHideOnly ? (
        // Systemowy layout: tylko oko (ukryj/pokaż). `pointerEvents:auto` przebija inertny
        // canvas (pointerEvents:none), dokładnie jak pasek zakładek.
        <span
          style={{ pointerEvents: 'auto' }}
          data-testid={`system-module-builder-element-${field.slug}-eye`}
        >
          <IconButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onToggleHidden?.(elementId);
            }}
            aria-label={
              hidden
                ? t('tenant.systemModuleBuilder.showField', { defaultValue: 'Pokaż pole' })
                : t('tenant.systemModuleBuilder.hideField', { defaultValue: 'Ukryj pole' })
            }
          >
            {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </IconButton>
        </span>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(elementId);
          }}
          aria-label={t('common.delete')}
          className="rounded p-1 text-text-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
          data-testid={`system-module-builder-element-${field.slug}-remove`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
